import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { INDSTOCKS_HOST } from '@/root/constants/common'

export const GS_SEARCH_FILTER_QUERY_PARAM_NAME = 'gsSearchFilter'
export const GS_SEARCH_QUERY_QUERY_PARAM_NAME = 'gsSearchQuery'

export function useGlobalSearchQueryParams(hostFromOptions?: string) {
  const router = useRouter()
  const { replace, pathname, query } = router
  const [location, setLocation] = useState<typeof window.location | null>(null)

  useEffect(() => {
    if (!hostFromOptions) {
      setLocation(window.location)
    }
  }, [hostFromOptions])

  const {
    [GS_SEARCH_FILTER_QUERY_PARAM_NAME]: gsSearchFilter,
    [GS_SEARCH_QUERY_QUERY_PARAM_NAME]: gsSearchQuery,
  } = router.query

  const host = hostFromOptions ?? location?.host
  const hostIsIndianStocks = host === INDSTOCKS_HOST
  const gsSearchFilterRetVal = hostIsIndianStocks ? 'IN_STOCKS' : gsSearchFilter

  const hostHandling = useCallback(() => {
    if (hostIsIndianStocks && gsSearchFilter !== 'IN_STOCKS') {
      void replace(
        {
          pathname,
          query: {
            ...query,
            [GS_SEARCH_FILTER_QUERY_PARAM_NAME]: gsSearchFilterRetVal,
          },
        },
        undefined,
        {
          shallow: true,
          scroll: true,
        },
      )
    }
  }, [
    gsSearchFilter,
    gsSearchFilterRetVal,
    hostIsIndianStocks,
    pathname,
    query,
    replace,
  ])

  const updateGsSearchQuery = useCallback(
    (inputValue?: string) => {
      const {
        [GS_SEARCH_QUERY_QUERY_PARAM_NAME]: gsSearchFilter,
        ...restQuery
      } = query

      void replace(
        {
          pathname,
          query: {
            ...restQuery,
            ...(inputValue
              ? { [GS_SEARCH_QUERY_QUERY_PARAM_NAME]: inputValue }
              : {}),
          },
        },
        undefined,
        {
          shallow: true,
          scroll: true,
        },
      )
    },
    [replace, pathname, query],
  )

  const removeGsSearchQuery = useCallback(() => {
    const { [GS_SEARCH_QUERY_QUERY_PARAM_NAME]: gsSearchFilter, ...restQuery } =
      query

    void replace(
      {
        pathname,
        query: restQuery,
      },
      undefined,
      {
        shallow: true,
        scroll: true,
      },
    )
  }, [pathname, query, replace])

  const updateGsFilterQuery = useCallback(
    (filterValue?: string) => {
      const {
        [GS_SEARCH_FILTER_QUERY_PARAM_NAME]: gsSearchQuery,
        ...restQuery
      } = query

      void replace(
        {
          pathname: router.pathname,
          query: {
            ...restQuery,
            ...(filterValue
              ? { [GS_SEARCH_FILTER_QUERY_PARAM_NAME]: filterValue }
              : {}),
          },
        },
        undefined,
        {
          shallow: true,
          scroll: true,
        },
      )
    },
    [query, replace, router.pathname],
  )

  const retVal = useMemo(() => {
    return {
      gsSearchFilter: gsSearchFilterRetVal,
      gsSearchQuery,
      updateGsSearchQuery,
      removeGsSearchQuery,
      updateGsFilterQuery,
      hostHandling,
    }
  }, [
    gsSearchFilterRetVal,
    gsSearchQuery,
    hostHandling,
    removeGsSearchQuery,
    updateGsFilterQuery,
    updateGsSearchQuery,
  ])

  return retVal
}
